<template>
    <div class="app-table-container">
        <div class="app-table-wrap">
            <table class="app-table">
                <thead>
                    <tr>
                        <th v-for="col in columns" :key="col.key" :style="col.width ? { width: col.width } : {}">
                            {{ col.label }}
                        </th>
                        <th v-if="$scopedSlots.action" class="th-action"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="pagedRows.length === 0">
                        <td :colspan="columns.length + ($scopedSlots.action ? 1 : 0)" class="td-empty">{{ $t('common.noData') }}</td>
                    </tr>
                    <tr v-for="(row, i) in pagedRows" :key="i">
                        <td v-for="col in columns" :key="col.key">
                            <slot :name="col.key" :row="row">{{ row[col.key] }}</slot>
                        </td>
                        <td v-if="$scopedSlots.action" class="td-action">
                            <slot name="action" :row="row" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="showPagination" class="app-table-pagination">
            <span class="pagination-info">
                {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }}
            </span>
            <div class="pagination-btns">
                <button class="pg-btn" :disabled="currentPage === 1" @click="goPage(currentPage - 1)">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                <button
                    v-for="p in pageNumbers"
                    :key="p"
                    class="pg-btn pg-num"
                    :class="{ active: p === currentPage }"
                    @click="goPage(p)"
                >{{ p }}</button>
                <button class="pg-btn" :disabled="currentPage === totalPages" @click="goPage(currentPage + 1)">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AppTable',
    props: {
        columns: { type: Array, required: true },
        rows: { type: Array, default: () => [] },
        pageSize: { type: Number, default: 10 },
        serverSide: { type: Boolean, default: false },
        total: { type: Number, default: 0 },
        lastPage: { type: Number, default: 0 },
        page: { type: Number, default: 1 }
    },
    data() {
        return { currentPage: 1 };
    },
    watch: {
        rows() {
            if (!this.serverSide) this.currentPage = 1;
        },
        page: {
            immediate: true,
            handler(val) {
                if (this.serverSide) this.currentPage = val;
            }
        }
    },
    computed: {
        totalCount() {
            return this.serverSide ? this.total : this.rows.length;
        },
        showPagination() {
            return this.totalCount > 0;
        },
        totalPages() {
            if (this.serverSide) {
                const byTotal = this.pageSize > 0 ? Math.ceil(this.totalCount / this.pageSize) : 0;
                if (this.lastPage > 0) return Math.max(this.lastPage, byTotal);
                return byTotal || 1;
            }
            return Math.ceil(this.totalCount / this.pageSize) || 1;
        },
        pagedRows() {
            if (this.serverSide) return this.rows;
            const start = (this.currentPage - 1) * this.pageSize;
            return this.rows.slice(start, start + this.pageSize);
        },
        pageNumbers() {
            const total = this.totalPages;
            const cur = this.currentPage;
            const delta = 2;
            const range = [];
            for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {
                range.push(i);
            }
            return range;
        }
    },
    methods: {
        goPage(p) {
            if (this.serverSide) {
                this.$emit('page-change', p);
            } else {
                this.currentPage = p;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.app-table-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.app-table-wrap {
    border-radius: 12px;
    border: 1px solid var(--border);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.app-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 480px;

    thead tr {
        background: var(--surface-1);
    }

    th {
        padding: 12px 16px;
        color: var(--text-sub);
        font-size: 13px;
        font-weight: 600;
        font-family: 'Urbanist', sans-serif;
        text-align: left;
        white-space: nowrap;
    }

    .th-action {
        width: 48px;
    }

    td {
        padding: 14px 16px;
        color: var(--text-main);
        font-size: 14px;
        font-weight: 500;
        font-family: 'Urbanist', sans-serif;
        border-top: 1px solid var(--surface-hover);
        white-space: nowrap;
    }

    .td-action {
        text-align: right;
        width: 48px;
    }

    .td-empty {
        text-align: center;
        color: var(--text-hint);
        font-size: 14px;
        font-family: 'Urbanist', sans-serif;
        padding: 32px 16px;
    }

    tbody tr:hover {
        background: color-mix(in srgb, var(--white) 2%, transparent);
    }
}

/* ── Pagination ── */
.app-table-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.pagination-info {
    color: var(--text-muted);
    font-size: 13px;
    font-family: 'Urbanist', sans-serif;
}

.pagination-btns {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pg-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border-radius: 6px;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-sub);
    font-size: 13px;
    font-weight: 600;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:hover:not(:disabled) {
        background: var(--surface-hover);
    }

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }

    &.active {
        background: linear-gradient(180deg, var(--tab-from) 0%, var(--tab-to) 100%);
        border-color: var(--accent);
        color: var(--text);
    }
}
</style>
